import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import CustomerCard from "./components/CustomerCard";
import OutstandingCard from "./components/OutstandingCard";
import LedgerDetailTable from "./components/LedgerDetailTable";
import { useLedgerFetch } from "./hooks/useLedgerFetch";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import CommonButtons from "src/shared/components/CommonButtons";
import LoadingScreen from "src/shared/components/LoadingScreen";
import { useUser } from "src/shared/context/UserContext";
// @ts-ignore: allow side-effect CSS import without module declarations
import "../../ledgers/Ledger.css";
import { getLedger } from "src/shared/services/servicesv2";
import LedgerTabHeader from "./components/LedgerTabHeader";
import { pageTitle } from "src/shared/utils/brand";
import PaymentCard from "./components/PaymentCard";
import { useUserTheme } from "src/shared/hooks/useUserTheme";
import { notifyError, notifyInfo } from "src/assets/toastService";
import AiMode from "src/assistant/AiMode";
import LedgerAiControl from "../ledgerAi/LedgerAiControl";
import CustomerSummary from "./CustomerSummary";

interface PaginationInfo {
  currentPage: number;
  pageSize: number;
  searchTerm: string;
  totalRecords: number;
}

const LedgerData: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const passedCustomers = location.state?.customers || [];
  const paginationInfo: PaginationInfo = location.state?.paginationInfo || {
    currentPage: 1,
    pageSize: 10,
    searchTerm: "",
    totalRecords: 0,
  };

  const fromPage = location.state?.fromPage || paginationInfo.currentPage || 1;
  const fromPageSize = location.state?.fromPageSize || paginationInfo.pageSize || 10;
  const fromSearchTerm = location.state?.searchTerm || paginationInfo.searchTerm || "";

  const [searchParams] = useSearchParams();
  const initialPage = Number(searchParams.get("pageNumber")) || 1;
  const { sidebarThemeClass } = useUserTheme();
  const [page, setPage] = useState(initialPage - 1);
  const { userConfigration, creditLimit } = useUser();
  const [rowsPerPage, setRowsPerPage] = useState(userConfigration?.tableDefaultSize || 10);

  const [customerPages, setCustomerPages] = useState<Map<number, any[]>>(new Map());
  const [loadingNav, setLoadingNav] = useState(false);
  const [lastCustomerPage, setLastCustomerPage] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<"Active" | "History">("Active");
  const includeHistory = activeTab === "History";
  const [sortField, setSortField] = useState<string>("orderedOn");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [open, setOpen] = useState<boolean>(false);

  const handleSortChange = (field: string) => {
    if (field === sortField) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setPage(0);
  };

  const { customerData, paginationData, loading, refetch, updatesLedgerLimit } = useLedgerFetch(
    customerId ?? "",
    page,
    rowsPerPage,
    includeHistory,
    sortField,
    sortOrder
  );

  const ledgerCreditLimit = customerData?.defaultCreditControl === true ? creditLimit?.defaultLimit ?? 0 : customerData?.creditLimit?.defaultLimit ?? 0;

  useEffect(() => {
    document.title = pageTitle("Ledger › Details");

    if (passedCustomers.length > 0) {
      setCustomerPages((prev) => {
        const updated = new Map(prev);
        updated.set(fromPage, passedCustomers);
        return updated;
      });
    }
  }, [passedCustomers, fromPage]);

  // FIX: Fetch next/prev page of customer list using list-level pageSize (fromPageSize)
  const fetchCustomerListPage = async (pageNumber: number) => {
    if (customerPages.has(pageNumber)) {
      return customerPages.get(pageNumber)!;
    }

    setLoadingNav(true);
    try {
      // Customer navigation always includes settled (zero-outstanding) customers,
      // even when the Ledger list was opened with them hidden.
      const shouldIncludeSettled = true;
      const res = await getLedger(pageNumber, fromPageSize, fromSearchTerm, undefined, undefined, shouldIncludeSettled);
      const data = res.data.result || [];
      setCustomerPages((prev) => new Map(prev).set(pageNumber, data));
      return data;
    } catch {
      notifyError("Failed to load customers");
      return [];
    } finally {
      setLoadingNav(false);
    }
  };

  const findPosition = () => {
    const pages = Array.from(customerPages.entries()).sort((a, b) => a[0] - b[0]);

    for (const [pageNum, customers] of pages) {
      const index = customers.findIndex((c: any) => c.customerId === customerId);
      if (index !== -1) {
        return {
          pageNum,
          index,
          nextId: customers[index + 1]?.customerId ?? null,
          prevId: customers[index - 1]?.customerId ?? null,
          hasNextInPage: index + 1 < customers.length,
          hasPrevInPage: index - 1 >= 0,
        };
      }
    }

    return {
      pageNum: fromPage,
      index: -1,
      nextId: null,
      prevId: null,
      hasNextInPage: false,
      hasPrevInPage: false,
    };
  };

  const position = findPosition();
  const pageNum = position.pageNum;
  const nextId = position.nextId;
  const prevId = position.prevId;
  const hasNextInPage = position.hasNextInPage;
  const hasPrevInPage = position.hasPrevInPage;

  const goNext = async () => {
    setLoadingNav(true);
    try {
      if (hasNextInPage && nextId) {
        navigateCustomer(nextId, pageNum);
        return;
      }

      const nextPageNum = pageNum + 1;
      const nextPageData = await fetchCustomerListPage(nextPageNum);
      if (nextPageData && nextPageData.length > 0) {
        navigateCustomer(nextPageData[0].customerId, nextPageNum);
      } else {
        setLastCustomerPage(pageNum);
        notifyInfo("No more customers");
      }
    } catch (error) {
      notifyError("Failed to load next customer");
      console.error("Error in goNext:", error);
    } finally {
      setLoadingNav(false);
    }
  };

  const goPrevious = async () => {
    setLoadingNav(true);
    try {
      if (hasPrevInPage && prevId) {
        navigateCustomer(prevId, pageNum);
        return;
      }

      const prevPageNum = pageNum - 1;
      if (prevPageNum < 1) {
        notifyInfo("No more customers");
        return;
      }

      // Fetch the previous page of customer list
      const prevPageData = await fetchCustomerListPage(prevPageNum);
      if (prevPageData && prevPageData.length > 0) {
        // Navigate to the last customer of the previous page
        navigateCustomer(prevPageData[prevPageData.length - 1].customerId, prevPageNum);
      } else {
        notifyInfo("No more customers");
      }
    } catch (error) {
      notifyError("Failed to load previous customer");
      console.error("Error in goPrevious:", error);
    } finally {
      setLoadingNav(false);
    }
  };

  const navigateCustomer = (targetCustomerId: string, pageNumber: number) => {
    navigate(`/v1/ledger/${targetCustomerId}`, {
      state: {
        customers: customerPages.get(pageNumber) || [],
        paginationInfo: {
          ...paginationInfo,
          currentPage: pageNumber,
        },
        fromPage: pageNumber,
        fromPageSize: fromPageSize,
        searchTerm: fromSearchTerm,
      },
      replace: true,
    });
  };

  const handleBack = () => {
    let url = `/v1/ledger?pageNumber=${fromPage}&pageSize=${fromPageSize}`;
    if (fromSearchTerm) url += `&searchBy=${encodeURIComponent(fromSearchTerm)}`;
    navigate(url, { state: { preserveSearch: true } });
  };

  const totalOutstanding = useMemo(() => {
    return (customerData?.totalOutstandingBalance ?? 0).toFixed(2);
  }, [customerData]);

  const isPrevDisabled = loadingNav || (pageNum === 1 && !hasPrevInPage);
  const isNextDisabled = loadingNav || lastCustomerPage === pageNum;

  function useIsMobileLayout(breakpoint = 1200) {
    const [isMobile, setIsMobile] = useState(
      typeof window !== "undefined" ? window.innerWidth < breakpoint : false
    );

    useEffect(() => {
      const handleResize = () => setIsMobile(window.innerWidth < breakpoint);
      window.addEventListener("resize", handleResize);
      handleResize(); // set correct value on mount
      return () => window.removeEventListener("resize", handleResize);
    }, [breakpoint]);

    return isMobile;
  }

  const isMobile = useIsMobileLayout(1200);

  return (
    <div className="bg-[#F8F9FA] p-1 thin-scrollbar w-full h-[92vh] overflow-auto">
      {(loading || loadingNav) && <LoadingScreen />}

      <div className="flex w-auto">
        {!loading && (
          <div className={`pt-1 shadow-inner rounded-lg ${sidebarThemeClass}`}>
            <div className="flex flex-col lg:flex-row items-center justify-between gap-2 w-[98%] mx-auto mb-2 px-2 lg:px-4 py-1.5 bg-white/80 backdrop-blur-md border border-gray-200 shadow-sm rounded-2xl">
              <CommonButtons label="BACK TO LIST" onClick={handleBack} variant="secondary" icon={KeyboardReturnIcon} />
              <h1 className="text-base md:text-lg lg:text-lg font-semibold text-gray-900 text-center flex-1">Outstanding Balance Details of Customer</h1>

              <div className="flex w-full lg:w-auto justify-between lg:justify-end gap-1.5">
                <CommonButtons label="Previous" variant="outlineDanger" onClick={goPrevious} disabled={isPrevDisabled} />
                <CommonButtons label="Next" variant="primary" onClick={goNext} disabled={isNextDisabled} className="w-28" />
              </div>
            </div>

            <div className="w-full mx-auto px-2 sm:px-4 mb-1.5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-4">
                {customerData && <CustomerCard customer={customerData} />}
                <OutstandingCard balance={totalOutstanding} onPay={() => { }} onRefund={() => { }} />
                {customerData && <PaymentCard customerData={customerData} customerId={customerId!} refetchLedger={refetch} />}
              </div>
            </div>

            <div className="ml-3 flex items-center justify-between gap-3">
              <LedgerTabHeader activeTab={activeTab} setActiveTab={setActiveTab} />
              {/* ✅ Customer Summary pushed to the far right of this row */}
              <div className="mr-4 w-full justify-end flex">
                <CustomerSummary customerId={customerId!} />
              </div>
            </div>

            {customerData && (
              <LedgerDetailTable
                ledger={includeHistory ? (customerData?.orderHistory as any[]) || [] : (customerData?.orderDetails as any[]) || []}
                page={page}
                rowsPerPage={rowsPerPage}
                paginationData={paginationData}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setPage(0);
                }}
                navigate={navigate}
                includeHistory={includeHistory}
                onSortChange={handleSortChange}
                sortField={sortField}
                sortOrder={sortOrder}
                customerData={customerData}
              />
            )}
          </div>
        )}
        {open && customerData && (
          <>
            {isMobile ? (
              // OVERLAY MODE (<1000px)
              <div className="fixed inset-0 z-50 flex justify-end bg-black/40">
                <div className="w-full sm:w-4/5 max-w-md h-full bg-white shadow-xl overflow-y-auto">
                  <LedgerAiControl
                    orderId={customerData.orderDetails?.[0]?.orderId ?? ""}
                    customerData={{
                      customerName: customerData.customerDetails.firstName ?? "",
                      customerId: String(customerData.customerDetails.refId) ?? "",
                      whatsappNo: customerData.customerDetails.phones[0].phone ?? "",
                      email: customerData.customerDetails.emails?.[0]?.email ?? "",
                      customerObjId: customerId ?? "",
                    }}
                    ledgerDetails={{
                      outstandingAmount: customerData.totalOutstandingBalance ?? 0,
                      orderAmount: customerData.orderDetails?.[0]?.orderAmount ?? 0,
                      creditLimit: ledgerCreditLimit,
                    }}
                    currency="QAR"
                    isCreditControlEnabled={userConfigration?.creditControlEnabled ?? false}
                    onUpdateCreditLimit={async (customerId, newLimit) => {
                      updatesLedgerLimit(customerId, newLimit);
                    }}
                  />
                </div>
              </div>
            ) : (
              // SIDE PANEL MODE (>=1000px)
              <div className="w-1/3">
                <LedgerAiControl
                  orderId={customerData.orderDetails?.[0]?.orderId ?? ""}
                  customerData={{
                    customerName: customerData.customerDetails.firstName ?? "",
                    customerId: String(customerData.customerDetails.refId) ?? "",
                    whatsappNo: customerData.customerDetails.phones[0].phone ?? "",
                    email: customerData.customerDetails.emails?.[0]?.email ?? "",
                    customerObjId: customerId ?? "",
                  }}
                  ledgerDetails={{
                    outstandingAmount: customerData.totalOutstandingBalance ?? 0,
                    orderAmount: customerData.orderDetails?.[0]?.orderAmount ?? 0,
                    creditLimit: ledgerCreditLimit,
                  }}
                  currency="QAR"
                  isCreditControlEnabled={userConfigration?.creditControlEnabled ?? false}
                  onUpdateCreditLimit={async (customerId, newLimit) => {
                    updatesLedgerLimit(customerId, newLimit);
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>

      <ToastContainer position="top-right" autoClose={3000} />
      <AiMode open={open} setOpen={setOpen} />
    </div>
  );
};

export default LedgerData;